import type { ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, unreachable, unreachableGaveUp, unreachableMessage, retryNow } = useAuth();
  const location = useLocation();

  if (unreachable && unreachableGaveUp && !user) {
    // Gave up (several rounds, or a definitive server answer): say what the
    // server said and offer a way out.
    return (
      <div role="alert" className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-lg font-medium">Can&apos;t restore your session right now</p>
        <p className="max-w-md text-sm opacity-70">{unreachableMessage ?? "The server isn't answering. Try again in a few minutes."}</p>
        <Link to="/login" className="rounded-md border px-4 py-2 text-sm font-medium">
          Sign in again
        </Link>
      </div>
    );
  }

  if (loading && unreachable) {
    // A deploy or outage on page load (stay signed in, 2026-10-22): say so
    // and keep retrying — never bounce a signed-in person to the sign-in page.
    return (
      <div role="alert" className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-lg font-medium">Can&apos;t reach Flow — retrying…</p>
        <p className="text-sm opacity-70">You&apos;re still signed in. This usually takes under a minute.</p>
        <button type="button" onClick={retryNow} className="rounded-md border px-4 py-2 text-sm font-medium">
          Retry now
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      // Full-screen loader on the dark hub background
      <div className="flex h-screen items-center justify-center bg-white">
        <div
          className="h-10 w-10 animate-pulse rounded-lg bg-paper"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <>{children}</>;
}
